import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Badge,
  ProgressBar,
  Button,
  Spinner
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

// Import Redux actions
import { commonSaveData } from "../../../../../../../redux/slices/commonSlice";
import { getReviwQueueDeshbordPath } from "../../../../../../../services/config";
import { currentFynencialYear } from '../../../../../../../services/commonServices';

const Hra = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [apiData, setApiData] = useState(null);
  const [apiError, setApiError] = useState(null);

  // Get User Context from LocalStorage
  const storedUser = JSON.parse(localStorage.getItem("tdsUser") || "{}");
  const staffId = storedUser.staffId || storedUser.id || storedUser.employeeId;

  // Get user info from localStorage
  const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
  const userId = userData.user_id || userData.id || "123";

  // 3. Fetch Data on Mount
  useEffect(() => {
    const fetchHRAData = async () => {
      if (!staffId) {
        console.log("No staffId found for HRA data");
        return;
      }

      setLoading(true);
      setApiError(null);

      try {
        const payload = {
          path: getReviwQueueDeshbordPath,
          data: {
            userId: userId,
            staffId: Number(staffId),
            employeeId: "FY" + currentFynencialYear(),
            section: "all",
            financialYear: currentFynencialYear(),
          },
        };

        console.log("Fetching HRA data:", payload);

        const response = await dispatch(commonSaveData(payload)).unwrap();

        console.log("HRA API Response:", response);

        if (response && response.code === 200 && response.status === true) {
          if (response.data) {
            setApiData(response.data);
          } else {
            throw new Error("No data field in response");
          }
        } else {
          throw new Error(response?.message || "Invalid API response");
        }

      } catch (error) {
        console.error("HRA Load Error:", error);
        setApiError(error);
        toast.error(error.message || "Failed to load HRA data");
      } finally {
        setLoading(false);
      }
    };

    fetchHRAData();
  }, [dispatch, staffId, userId]);

  // 4. Loading State
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center h-100">
        <Spinner animation="border" variant="primary" />
        <span className="ms-3 text-white">Loading HRA Data...</span>
      </div>
    );
  }

  // 5. Error State
  if (apiError) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="card bg-dark border-danger">
              <div className="card-header bg-danger text-white">
                <h4 className="mb-0">Error Loading HRA Data</h4>
              </div>
              <div className="card-body">
                <p className="text-white">{apiError.message}</p>
                <p className="text-gray small">
                  API Endpoint: {getReviwQueueDeshbordPath}
                </p>
                <button 
                  className="btn btn-primary mt-3"
                  onClick={() => {
                    setApiError(null);
                    setLoading(true);
                    setTimeout(() => {
                      window.location.reload();
                    }, 500);
                  }}
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No staffId provided
  if (!staffId) {
    return (
      <div className="text-center py-5">
        <div className="card bg-dark border-warning" style={{ maxWidth: '500px', margin: '0 auto' }}>
          <div className="card-header bg-warning text-dark">
            <h4 className="mb-0">No Employee Selected</h4>
          </div>
          <div className="card-body">
            <p className="color-gray">
              Please select an employee from the TDS employee list to view HRA details.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // No API data
  if (!apiData) {
    return (
      <div className="text-center py-5">
        <div className="card bg-dark border-info" style={{ maxWidth: '500px', margin: '0 auto' }}>
          <div className="card-header bg-info text-white">
            <h4 className="mb-0">No HRA Data Available</h4>
          </div>
          <div className="card-body">
            <p className="color-gray">
              Unable to load HRA data for the selected employee.
            </p>
            <div className="mt-3">
              <p className="small color-gray">
                Employee ID: <strong>{staffId}</strong><br />
                Financial Year: <strong>{currentFynencialYear()}</strong>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 6. Safe Data Extraction
  const employeeInfo = apiData.employeeInfo || {};
  const sections = apiData.sections || {};
  const overallStatus = apiData.overallStatus || {};
  const financialYear = employeeInfo.financialYear || currentFynencialYear();

  // Extract HRA section data
  const hraSection = sections.hra || {};
  const hraFields = hraSection.fields || {};
  const rentDetails = hraSection.rentDetails || {};
  const landlordDetails = hraSection.landlordDetails || {};
  const hraDocuments = hraSection.documents || [];

  // Map API Values to UI Variables
  const actualHra = parseInt(hraFields.actualHRAReceived?.value) || 0;
  const rentPaid = parseInt(hraFields.rentPaid?.value) || 0;
  const basicSalaryPercent = parseInt(hraFields.basicSalaryPercent?.value) || 0;
  const annualHRAExemption = parseInt(hraFields.annualHRAExemption?.value) || 0;
  
  const monthlyRent = parseInt(rentDetails.monthlyRentAmount) || 0;
  const totalRentPaid = parseInt(rentDetails.totalRentPaid) || rentPaid;
  
  const cityCategory = "Metro"; // Default, should come from API
  const isMetro = cityCategory === "Metro";

  // Calculations
  const tenPercentBasic = basicSalaryPercent || (actualHra * 0.1);
  const fiftyPercentSalary = isMetro ? actualHra * 0.5 : actualHra * 0.4;
  
  // Least of three calculation
  const rentMinus10Percent = Math.max(0, rentPaid - tenPercentBasic);
  const leastOfThree = Math.min(
    actualHra,
    rentMinus10Percent,
    fiftyPercentSalary
  );
  
  // Use API exemption or calculated
  const finalExemption = annualHRAExemption || leastOfThree;
  const monthlyExemption = Math.round(finalExemption / 12);

  // Document Logic
  const hasRentReceipts = hraDocuments.some(doc => 
    doc.type === "rent_receipt" && doc.status === "uploaded"
  );
  const hasLandlordPAN = hraDocuments.some(doc => 
    doc.type === "landlord_pan" && doc.status === "uploaded"
  );

  // Get uploaded rent receipt URL
  const rentReceiptUrl = hraDocuments.find(doc => 
    doc.type === "rent_receipt" && doc.url
  )?.url;

  return (
    <div className="p-4" style={{ height: 'calc(100% - 100px)', overflowY: 'auto' }}>
      {/* Header */}
      <Row className="mb-4 align-items-center">
        <Col>
          <h4 className="text-white mb-1">
            {employeeInfo.employeeName || storedUser.employeeName || "Employee"} · FY {financialYear}
          </h4>
          <div className="mt-2">
            <Badge bg="primary" className="me-2">PAN: {employeeInfo.employeePAN || "N/A"}</Badge>
            <Badge bg="info" className="me-2">{cityCategory} City</Badge>
            <Badge bg={hraSection.status === 'pending' ? 'warning' : hraSection.status === 'approved' ? 'success' : 'danger'} className="text-white">
              {hraSection.status?.toUpperCase() || 'PENDING'}
            </Badge>
          </div>
        </Col>
      </Row>

      <Row>
        {/* Left Column */}
        <Col lg={8}>
          {/* HRA Calculation */}
          <Card className="bg-dark text-light border-secondary mb-4">
            <Card.Header className="bg-purple text-white">
              <h5 className="mb-0">HRA Exemption Review</h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted mb-4">
                Review and verify House Rent Allowance exemption as per Section 10(13A)
              </p>

              <Row className="g-4">
                <Col md={6}>
                  <div className="d-flex justify-content-between py-2 border-bottom border-secondary">
                    <span>Actual HRA Received (annual)</span>
                    <strong>₹{actualHra.toLocaleString('en-IN')}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-2 border-bottom border-secondary">
                    <span>Rent Paid - 10% of Basic</span>
                    <strong>₹{rentMinus10Percent.toLocaleString('en-IN')}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-2 border-bottom border-secondary">
                    <span>{isMetro ? "50%" : "40%"} of Basic ({isMetro ? "Metro" : "Non-Metro"})</span>
                    <strong>₹{fiftyPercentSalary.toLocaleString('en-IN')}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-2 border-bottom border-secondary">
                    <span>Least of Three (Exemption)</span>
                    <strong className="text-success">₹{leastOfThree.toLocaleString('en-IN')}</strong>
                  </div>
                </Col>

                <Col md={6}>
                  <Card className="bg-gradient text-white border-0 text-center" style={{ background: "linear-gradient(135deg, #6a11cb, #2575fc)" }}>
                    <Card.Body className="py-5">
                      <h6 className="mb-3">Approved Exemption</h6>
                      <h2 className="display-5 fw-bold mb-3">₹{finalExemption.toLocaleString('en-IN')}</h2>
                      <p className="mb-2 fs-5">Annual HRA Exemption</p>
                      <p className="fs-4 opacity-75">₹{monthlyExemption.toLocaleString('en-IN')} / month</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Rent & Landlord Details */}
          <Row className="g-4">
            <Col md={6}>
              <Card className="bg-dark text-light border-secondary mb-4">
                <Card.Header>Rent Details</Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <strong>Monthly Rent Amount:</strong> ₹{monthlyRent.toLocaleString('en-IN')}
                  </div>
                  <div className="mb-3">
                    <strong>Total Rent Paid (Annual):</strong> ₹{totalRentPaid.toLocaleString('en-IN')}
                  </div>
                  <div>
                    <strong>Rental Period:</strong> 12 months
                  </div>
                </Card.Body>
              </Card>

              <Card className="bg-dark text-light border-secondary">
                <Card.Header>Rental Property Address</Card.Header>
                <Card.Body>
                  <p className="mb-0 text-muted">
                    {hraSection.rentalPropertyAddress || "Address not provided"}
                  </p>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="bg-dark text-light border-secondary mb-4">
                <Card.Header>Landlord Details</Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <strong>Name:</strong> {landlordDetails.name || "N/A"}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <strong>PAN:</strong>
                    <span>{landlordDetails.pan || "N/A"}</span>
                    {totalRentPaid > 100000 && !landlordDetails.pan && (
                      <Badge bg="danger">PAN Required (₹1L+)</Badge>
                    )}
                  </div>
                  <div className="mt-3">
                    <strong>Verification Status:</strong> 
                    <Badge bg={hasLandlordPAN ? "success" : "warning"} className="ms-2">
                      {hasLandlordPAN ? "PAN Uploaded" : "PAN Pending"}
                    </Badge>
                  </div>
                </Card.Body>
              </Card>

              <Card className="bg-dark text-light border-secondary">
                <Card.Header>Validation Status</Card.Header>
                <Card.Body>
                  <div className="d-flex flex-wrap gap-3 mb-3">
                    <Badge bg="success" pill>Formula Checked</Badge>
                    {hraFields.actualHRAReceived?.status === 'verified' && (
                      <Badge bg="success" pill>HRA Verified</Badge>
                    )}
                    {hraFields.rentPaid?.status === 'verified' && (
                      <Badge bg="success" pill>Rent Verified</Badge>
                    )}
                    {(totalRentPaid > 100000 && !hasLandlordPAN) && (
                      <Badge bg="danger" pill>PAN Missing</Badge>
                    )}
                    {!hasRentReceipts && (
                      <Badge bg="warning" pill>Receipts Pending</Badge>
                    )}
                  </div>
                  <div className="small text-muted">
                    Completion: {hraSection.completionPercentage || 0}%
                    <ProgressBar 
                      now={hraSection.completionPercentage || 0} 
                      variant="info"
                      className="mt-1"
                    />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>

        {/* Right Column */}
        <Col lg={4}>
          {/* Rent Receipts */}
          <Card className="bg-dark text-light border-secondary mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>Rent Receipts</span>
              {rentReceiptUrl && (
                <a href={rentReceiptUrl} target="_blank" rel="noreferrer">
                  <Button size="sm" variant="outline-primary">View Document</Button>
                </a>
              )}
            </Card.Header>
            <Card.Body>
              <div className="text-center mb-4">
                <ProgressBar 
                  now={hasRentReceipts ? 100 : 0} 
                  label={hasRentReceipts ? "Uploaded" : "Pending"} 
                  variant={hasRentReceipts ? "success" : "warning"}
                  style={{ height: "30px", fontSize: "14px" }}
                />
              </div>
              <div className="text-center mb-3">
                {hasRentReceipts ? (
                  <p className="text-success">
                    <i className="bi bi-check-circle me-2"></i>
                    Rent Receipts Available
                  </p>
                ) : (
                  <p className="text-muted">
                    <i className="bi bi-exclamation-circle me-2"></i>
                    No receipts uploaded yet
                  </p>
                )}
              </div>
              
              {/* Document List */}
              <div className="mt-3">
                <h6 className="text-white mb-2">Required Documents:</h6>
                <ul className="list-unstyled">
                  {hraDocuments.map((doc, index) => (
                    <li key={index} className="mb-2 d-flex justify-content-between align-items-center">
                      <span className="text-gray">{doc.name}</span>
                      <Badge bg={doc.status === 'uploaded' ? 'success' : 'warning'}>
                        {doc.status}
                      </Badge>
                    </li>
                  ))}
                </ul>
              </div>
            </Card.Body>
          </Card>

          {/* Action Buttons */}
          <Card className="bg-dark text-light border-secondary">
            <Card.Header>Review Actions</Card.Header>
            <Card.Body className="d-grid gap-3">
              {hraSection.actions?.canApprove && (
                <Button variant="success" size="lg" onClick={() => {
                  toast.success("HRA Approved Successfully");
                  // Add your approval API call here
                }}>
                  Approve HRA
                </Button>
              )}
              
              {hraSection.actions?.canReject && (
                <Button variant="danger" size="lg" onClick={() => {
                  toast.warning("HRA Rejected");
                  // Add your rejection API call here
                }}>
                  Reject HRA
                </Button>
              )}
              
              <Button variant="outline-info" size="lg" onClick={() => {
                // Add comment functionality
                const comment = prompt("Enter your comment:");
                if (comment) {
                  toast.info("Comment added");
                }
              }}>
                Add Comment
              </Button>
              
              <Button variant="outline-secondary" size="lg" onClick={() => {
                setLoading(true);
                setTimeout(() => {
                  window.location.reload();
                }, 500);
              }}>
                Refresh Data
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Hra;